import { type PropsWithChildren } from 'react';
import classNames from 'classnames';

import { type Repub, REPUBS } from '@cx-modules/constants';

import Logo from '~/components/Logo';
import { Picture } from '~/components/ui/Picture';
import { useServicesContext } from '~/providers/services-context.tsx';
import { removeLeadingSlash } from '~/utils/removeLeadingSlash';

import facebook from './img/facebook.svg';
import instagram from './img/instagram.svg';
import twentyOne from './img/twentyOne.svg';
import x from './img/X.svg';
import styles from './FooterUS.module.css';

const getImage = (url: string): string | null => {
  switch (true) {
    case url.includes('facebook'):
      return facebook;
    case url.includes('instagram'):
      return instagram;
    case url.includes('x.com'):
    case url.includes('twitter'):
      return x;
    default:
      return null;
  }
};

export interface FooterUSProps extends PropsWithChildren {
  generalLinks: {name: string;url: string;}[];
  repub: Repub;
  resourcesUrl: string;
  socialLinks: {name: string;url: string;}[];
}

export const FooterUsStyle: React.FC<FooterUSProps> = ({
  children,
  generalLinks,
  repub,
  resourcesUrl,
  socialLinks
}) => {
  const { labelActions } = useServicesContext();

  return (
    <>
			<section className={styles.footerUSWrapper} data-testid="footer-us">
				<div
          className={classNames(styles.footerUSContainer, {
            [styles.hasAdditionalComponent]: !!children
          })}>

					<div className={styles.footerUSLogoContainer}>
						<a
              href="./"
              rel="noopener noreferrer"
              target="_blank"
              title="Oddschecker">

							<Logo resourcesUrl={resourcesUrl} />
						</a>
					</div>
					<div
            className={styles.footerUSRegulationContainer}
            data-testid="disclaimer">

						{repub === REPUBS.US.name ?
            <Picture
              altText="21+ icon"
              className={styles.footerUSTwentyOne}
              svgSrc={twentyOne} /> :


            <span className={styles.legalAge}>18+</span>
            }
						{labelActions.get('RESPONSIVE_FOOTER_LEGAL')}
					</div>
					{children && <div className={styles.additionalChild}>{children}</div>}
					<div className={styles.footerUSSocialsContainer}>
						{socialLinks.map(({ name, url }) => {
              const image = getImage(url);
              return image ?
              <a
                className={styles.footerUSSocialsLink}
                data-testid="social-link"
                href={url}
                key={name}
                rel="noopener noreferrer"
                target="_blank"
                title={name === 'twitter' ? 'X' : name}>

									<Picture
                  altText={`${name} icon`}
                  className={styles.footerUSSocialsImage}
                  svgSrc={image} />

								</a> :
              null;
            })}
					</div>
				</div>
			</section>
			<section className={styles.footerUSNavWrapper}>
				<nav aria-label="Footer" className={styles.footerUSNavContainer}>
					{generalLinks.map(({ name, url }) =>
          <a
            className={styles.footerUSNavItem}
            data-testid="general-link"
            href={removeLeadingSlash(url)}
            key={name}>

							{name}
						</a>
          )}
				</nav>
			</section>
		</>);

};